/* eslint-disable react/require-default-props */
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { translate } from '@utils/i18n';
import { Label } from '../../../../atoms/Label';
import { Popup } from '../../../Popup';
import { CheckboxList } from '../../CheckboxList';
import './SelectInputStyles.scss';
/**
 * SelectInput Component
 * Form select input with popup-based dropdown
 * Supports both single and multi-select with optional search
 *
 * @note Multi-select mode (multiple=true) only supports primitive values (string | number).
 * Using object values with multi-select will throw a runtime error.
 *
 * @version 1.0.0
 * @since 2026-02-05
 */
export function SelectInput({ id, name, label, required = false, error, disabled = false, options, value, values = [], onChange, onMultiChange, multiple = false, showSearch = false, searchPlaceholder, displayMode = 'labels', maxHeight = 300, className }) {
    // Runtime validation: multi-select only supports primitive values
    if (multiple && values.length > 0) {
        const firstValue = values[0];
        if (typeof firstValue !== 'string' && typeof firstValue !== 'number') {
            throw new Error('SelectInput: Multi-select mode (multiple=true) only supports primitive values (string | number). ' +
                'Object values are not supported. Please use single-select mode for object values or convert to primitive values.');
        }
    }
    // Translation namespace for UI components
    const namespace = 'ui.select_input';
    // Translation keys for user-visible strings
    const t = {
        search: searchPlaceholder || translate('search_placeholder', { namespace }),
        selectPlaceholder: translate('select_placeholder', { namespace }),
        itemsSelected: (count) => count === 1 ? translate('item_selected', { namespace }) : translate('items_selected', { namespace, count }),
        noOptionsFound: translate('no_options_found', { namespace })
    };
    const [visible, setVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const anchor = useRef(null);
    const searchInputRef = useRef(null);
    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!showSearch || !searchTerm.trim()) {
            return options;
        }
        const searchLower = searchTerm.toLowerCase().trim();
        return options.filter((option) => option.label.toLowerCase().includes(searchLower));
    }, [options, searchTerm, showSearch]);
    // Focus search input when dropdown opens
    useEffect(() => {
        if (visible && showSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [visible, showSearch]);
    // ClickAway fires on trigger mousedown (trigger is outside the popup). Lock
    // when already open so re-clicking the trigger closes instead of re-opening.
    const closingViaTriggerRef = useRef(false);
    const handleTriggerMouseDown = () => {
        if (disabled)
            return;
        if (visible)
            closingViaTriggerRef.current = true;
    };
    const handleOpen = () => {
        if (disabled)
            return;
        if (closingViaTriggerRef.current) {
            closingViaTriggerRef.current = false;
            setVisible(false);
            setSearchTerm('');
            return;
        }
        setVisible(true);
        setSearchTerm('');
    };
    const handleClose = () => {
        if (closingViaTriggerRef.current)
            return;
        setVisible(false);
        setSearchTerm('');
    };
    const handleSingleSelect = (selectedValue) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(selectedValue);
        handleClose();
    };
    const handleMultiSelect = (selectedValue) => {
        const newValues = values.includes(selectedValue)
            ? values.filter((v) => v !== selectedValue)
            : [...values, selectedValue];
        onMultiChange === null || onMultiChange === void 0 ? void 0 : onMultiChange(newValues);
    };
    // Get display text for select trigger
    const getDisplayText = () => {
        if (multiple) {
            if (values.length === 0) {
                return t.selectPlaceholder;
            }
            if (displayMode === 'count') {
                return `${values.length} ${t.itemsSelected(values.length)}`;
            }
            if (displayMode === 'summary') {
                const firstOption = options.find((opt) => opt.value === values[0]);
                if (values.length === 1) {
                    return (firstOption === null || firstOption === void 0 ? void 0 : firstOption.label) || t.selectPlaceholder;
                }
                return `${(firstOption === null || firstOption === void 0 ? void 0 : firstOption.label) || t.selectPlaceholder} +${values.length - 1} more`;
            }
            // displayMode === 'labels' (default)
            const selectedLabels = options.filter((opt) => values.includes(opt.value)).map((opt) => opt.label);
            return selectedLabels.join(', ');
        }
        const selectedOption = options.find((opt) => opt.value === value);
        return (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) || t.selectPlaceholder;
    };
    return (React.createElement("div", { className: `qmb-ui-select ${className || ''} ${error ? 'has-error' : ''}` },
        React.createElement("div", { ref: anchor, className: `qmb-ui-select__trigger ${disabled ? 'qmb-ui-select__trigger--disabled' : ''}`, onMouseDown: handleTriggerMouseDown, onClick: handleOpen, onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen();
                }
            }, role: "button", tabIndex: disabled ? -1 : 0, "aria-expanded": visible, "aria-haspopup": "listbox", "aria-disabled": disabled }, getDisplayText()),
        label && (React.createElement(Label, { className: `qmb-ui-label ${required ? '--required' : ''}`, htmlFor: id }, label)),
        React.createElement(Popup, { position: "bottom-left", anchor: anchor.current, show: visible, onClose: handleClose, containerClass: "qmb-ui-popup--action-list qmb-ui-select__popup", containerStyle: { maxHeight: `${maxHeight}px` } },
            showSearch && (React.createElement("div", { className: "qmb-ui-select__search" },
                React.createElement("input", { ref: searchInputRef, type: "text", className: "qmb-ui-select__search-input", placeholder: t.search, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), "aria-label": t.search }))),
            React.createElement("div", { className: "qmb-ui-select__options", style: { maxHeight: `${maxHeight - (showSearch ? 60 : 0)}px` } }, multiple ? (React.createElement(CheckboxList, { options: filteredOptions.map((opt) => ({ title: opt.label, value: opt.value })), value: values, onChange: (selectedValue) => handleMultiSelect(selectedValue), label: "" })) : (React.createElement("ul", null, filteredOptions.length === 0 ? (React.createElement("li", null,
                React.createElement("button", { type: "button", className: "qmb-ui-select__option qmb-ui-select__option--empty" }, t.noOptionsFound))) : (filteredOptions.map((option, index) => {
                var _a;
                // Use option.id if provided, otherwise fall back to index for stable keys
                const optionKey = (_a = option.id) !== null && _a !== void 0 ? _a : `option-${index}`;
                return (React.createElement("li", { key: optionKey },
                    React.createElement("button", { type: "button", className: `qmb-ui-select__option ${value === option.value ? 'qmb-ui-select__option--selected' : ''}`, onClick: () => handleSingleSelect(option.value) }, option.label)));
            })))))),
        error && React.createElement("span", { className: "error-message" }, error),
        name && !multiple && React.createElement("input", { type: "hidden", name: name, value: value !== undefined ? String(value) : '' }),
        name && multiple && values.map((v, i) => React.createElement("input", { key: i, type: "hidden", name: `${name}[]`, value: String(v) }))));
}
